<script>
import bem from '@/utils/bem';
import parseModuleContent from '@/utils/parseModuleContent';
import { mapActions, mapState } from 'vuex';
import { CloseOutlined } from '@ant-design/icons-vue';
import CheckCircleIcon from '@/components/svg/CheckCircleIcon.vue';
import DropdownArrowIcon from '@/components/svg/DropdownArrowIcon.vue';

const b = bem('product-select-module');

export default {
  components: {
    CloseOutlined,
    CheckCircleIcon,
    DropdownArrowIcon,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
    blockIndex: {
      type: Number,
      default: 0,
    },
    blockBackground: {
      type: String,
      default: 'transparent',
    },
    companyColors: {
      type: Object,
      default: () => ({
        color1: '',
        color2: '',
        color3: '',
      }),
    },
  },
  emits: ['update', 'select', 'selection-change'],
  data() {
    return {
      selectedCollectionIds: [],
      isDropdownOpen: false,
    };
  },
  computed: {
    ...mapState({
      collections: (state) => state.collections.companyCollections,
      currentCompany: (state) => state.companies.currentCompany,
    }),
    content() {
      return parseModuleContent(this.moduleData);
    },
    selectedCollections() {
      return this.selectedCollectionIds
        .map((id) => this.collections.find((c) => c.id === id))
        .filter(Boolean);
    },
    getCollectionProducts() {
      return (collection) => collection.products || [];
    },
    blockType() {
      return this.content?.blockType || null;
    },
    showItemsBlock() {
      if (!this.blockType) return true;
      return this.blockType === 'items';
    },
  },
  watch: {
    'content.collectionIds': {
      handler(newIds) {
        if (newIds && Array.isArray(newIds)) {
          this.selectedCollectionIds = [...newIds];
          // После загрузки коллекций обновляем content с актуальным startWithColor
          this.$nextTick(() => {
            this.emitSelectionChange();
          });
        }
      },
      immediate: true,
    },
    companyId: {
      handler(newCompanyId) {
        if (newCompanyId) {
          this.getCompanyCollectionsAction(newCompanyId);
        }
      },
      immediate: true,
    },
    blockIndex: {
      handler() {
        if (this.selectedCollectionIds.length > 0) {
          this.emitSelectionChange();
        }
      },
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getCompanyCollectionsAction: 'collections/getCompanyCollections',
    }),
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    isSelected(collectionId) {
      return this.selectedCollectionIds.includes(collectionId);
    },
    toggleCollection(collection) {
      const index = this.selectedCollectionIds.indexOf(collection.id);
      if (index === -1) {
        this.selectedCollectionIds.push(collection.id);
      } else {
        this.selectedCollectionIds.splice(index, 1);
      }
      this.emitSelectionChange();
    },
    removeCollection(collectionId) {
      const index = this.selectedCollectionIds.indexOf(collectionId);
      if (index !== -1) {
        this.selectedCollectionIds.splice(index, 1);
      }
      this.emitSelectionChange();
    },
    emitSelectionChange() {
      const startWithColor = this.blockIndex % 2 === 1;
      const content = {
        type: 'collection',
        collectionIds: this.selectedCollectionIds,
        backgroundColor: this.companyColors.color3 || '',
        startWithColor,
      };
      this.$emit('selection-change', {
        hasSelection: this.selectedCollectionIds.length > 0,
        selectionCount: this.selectedCollectionIds.length,
        templateId: 'product-select',
        type: 'product',
        content: JSON.stringify(content),
        parsedContent: content,
      });
    },
    getCollectionBackground(collectionIndex) {
      if (this.isPopup || this.isEditMode) return 'transparent';
      const isBlockTransparent = this.blockBackground === 'transparent';
      if (collectionIndex % 2 === 0) {
        return this.blockBackground;
      }
      return isBlockTransparent ? (this.companyColors.color3 || 'transparent') : 'transparent';
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup, preview: !isEditMode })">
    <div :class="b('header')" v-if="isPopup">
      <div :class="b('select-wrapper')">
        <div
          :class="b('select')"
          role="button"
          tabindex="0"
          @click="toggleDropdown"
          @keydown.enter="toggleDropdown"
        >
          <span :class="b('select-placeholder')">
            {{ selectedCollectionIds.length > 0
              ? `${selectedCollectionIds.length} selected`
              : 'Select Collections' }}
          </span>
          <DropdownArrowIcon :class="b('select-arrow', { open: isDropdownOpen })" />
        </div>
        <div v-if="isDropdownOpen" :class="b('dropdown')">
          <div
            v-for="collection in collections"
            :key="collection.id"
            :class="b('dropdown-item')"
            role="button"
            tabindex="0"
            @click="toggleCollection(collection)"
            @keydown.enter="toggleCollection(collection)"
          >
            <span :class="b('dropdown-item-name', { selected: isSelected(collection.id) })">
              {{ collection.name }}
            </span>
            <CheckCircleIcon v-if="isSelected(collection.id)" :class="b('check-icon')" />
          </div>
          <div v-if="collections.length === 0" :class="b('dropdown-empty')">
            No collections available
          </div>
        </div>
      </div>
      <div :class="b('selected-tags')">
        <div
          v-for="collection in selectedCollections"
          :key="collection.id"
          :class="b('tag')"
        >
          <span :class="b('tag-name')">{{ collection.name }}</span>
          <CloseOutlined :class="b('tag-remove')" @click="removeCollection(collection.id)" />
        </div>
      </div>
    </div>

    <div
      v-if="showItemsBlock && selectedCollections.length > 0"
      :class="b('content', { mobile: isMobile, popup: isPopup, preview: !isEditMode && !isPopup})"
    >
      <template v-if="selectedCollections.length > 0">
        <div
          v-for="(collection, collectionIndex) in selectedCollections"
          :key="collection.id"
          :class="b('collection-block', { preview: !isEditMode && !isPopup, mobile: isMobile })"
          :style="{ backgroundColor: getCollectionBackground(collectionIndex) }"
        >
          <div :class="b('collection-info', { mobile: isMobile })">
            <div :class="b('collection-name', { mobile: isMobile, popup: isPopup })">
              {{ collection.name }}
            </div>
            <div :class="b('collection-desc', { mobile: isMobile })">
              {{ collection.description }}
            </div>
          </div>
          <div :class="b('products-grid', { mobile: isMobile, popup: isPopup })">
            <div
              v-for="product in getCollectionProducts(collection)"
              :key="product.id"
              :class="b('product-card', { mobile: isMobile, popup: isPopup })"
            >
              <img
                :class="b('product-image', { mobile: isMobile, popup: isPopup })"
                :src="product.imageUri"
                :alt="product.displayName"
              />
              <div :class="b('product-name', { mobile: isMobile, popup: isPopup })">
                {{ product.displayName }}
              </div>
              <div :class="b('product-desc', { mobile: isMobile, popup: isPopup })">
                {{ product.subName }}
              </div>
            </div>
            <div
              v-if="getCollectionProducts(collection).length === 0"
              :class="b('empty-products')"
            >
              No products in this collection
            </div>
          </div>
        </div>
      </template>
      <div v-else :class="b('empty-preview')">
        Select a collection to see preview
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.product-select-module {
  width: 1540px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    gap: vw-full(40);
  }

  &_popup {
    width: 1200px;
    gap: 24px;
    margin-bottom: 24px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
      gap: vw-full(24);
      margin-bottom: vw-full(24);
    }
  }

  &_preview {
    width: 100%;
    padding: 0;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 40px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(40);
    }
  }

  &__select-wrapper {
    position: relative;
    width: 400px;
    height: 56px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(400);
      height: vw-full(56);
    }
  }

  &__select {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 20px;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s;
    position: initial;
    z-index: 11;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(56);
      padding: vw-full(13) vw-full(20);
      border-radius: vw-full(12);
    }

    &:hover {
      border-color: #5f8aa4;
    }
  }

  &__select-placeholder {
    width: 100%;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    color: #242424;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
  }

  &__select-arrow {
    font-size: 10px;
    color: #7f7f7f;
    transition: transform 0.2s;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(10);
    }

    &_open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
    padding-top: 10px;
    margin-top: -10px;

    @include mediaMinMax(1025, $normal) {
      max-height: vw-full(240);
      border-radius: vw-full(12);
    }
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    transition: background 0.2s;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(10) vw-full(16);
    }

    &:hover {
      background: #f5f5f5;
    }
  }

  &__dropdown-item-name {
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    color: #595959;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }

    &_selected {
      color: #293241;
      font-weight: 500;
    }
  }

  &__check-icon {
    color: #5f8aa4;
    font-size: 14px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
    }
  }

  &__dropdown-empty {
    padding: 16px;
    text-align: center;
    color: #7f7f7f;
    font-family: Montserrat, sans-serif;
    font-size: 14px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(16);
      font-size: vw-full(14);
    }
  }

  &__selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
    flex: 1;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8) vw-full(20);
    }
  }

  &__tag {
    width: 240px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 12px;
    border: 1px solid #d9d9d9;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
      border-radius: vw-full(12);
    }
  }

  &__tag-name {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    line-height: normal;
    color: #595959;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__tag-remove {
    position: absolute;
    top: 5px;
    right: 12px;
    font-size: 10px;
    line-height: 1;
    color: #595959;
    cursor: pointer;
    transition: color 0.2s;

    @include mediaMinMax(1025, $normal) {
      top: vw-full(5);
      right: vw-full(12);
      font-size: vw-full(10);
    }
А
    &:hover {
      color: #ff4d4f;
    }
  }

  &__content {
    min-height: 300px;
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 64px;

    @include mediaMinMax(1025, $normal) {
      min-height: vw-full(300);
      gap: vw-full(64);
    }

    &_preview {
      gap: 0;
    }

    &_mobile {
      min-height: initial;
    }
  }

  &__collection-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 0;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(20);
    }

    &_popup {
      padding: 0;
    }

    &_preview {
      padding: 64px 0;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(64) 0;
      }
    }

    &_mobile {
      padding: 40px 0;
      gap: 16px;
    }
  }

  &__collection-info {
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(20);
    }

    &_mobile {
      gap: 16px;
    }
  }

  &__collection-name {
    font-family: Montserrat, sans-serif;
    color: #293241;
    text-align: center;
    font-size: 40px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: capitalize;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    &_popup {
      font-size: 28px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(28);
      }
    }

    &_mobile {
      font-size: 28px;
      font-weight: 500;
    }
  }

  &__collection-desc {
    font-family: Montserrat, sans-serif;
    color: #5e5d5d;
    text-align: center;
    font-size: 19px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }

    &_mobile {
      font-size: 16px;
    }
  }

  &__products-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 32px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(32);
    }

    &_popup {
      gap: 24px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(24);
      }
    }

    &_mobile {
      flex-direction: column;
      gap: 28px;
    }
  }

  &__product-card {
    border-radius: 18px 3px;
    background: #FFF;
    box-shadow: 4px 4px 8px 0 rgba(95, 138, 164, 0.20);
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      border-radius: vw-full(18) vw-full(3);
      padding: vw-full(8);
    }

    &_popup {
      padding: 7px;
      gap: 11px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(11);
        padding: vw-full(7);
      }
    }

    &_mobile {
      gap: 8px;
      padding: 4px;
      border-radius: 12px 3px;
      box-shadow: 3px 3px 6px 0 rgba(95, 138, 164, 0.20);
    }
  }

  &__product-image {
    width: 468px;
    height: 351px;
    border-radius: 12px 3px;
    aspect-ratio: 1;
    object-fit: cover;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(468);
      height: vw-full(351);
      border-radius: vw-full(12) vw-full(3);
    }

    &_popup {
      width: 370px;
      height: 278px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(370);
        height: vw-full(278);
      }
    }

    &_mobile {
      width: 348px;
      height: 261px;
    }
  }

  &__product-name {
    width: 100%;
    font-family: Montserrat, sans-serif;
    color: #293241;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 26px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(26);
    }

    &_popup {
      font-size: 23px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(23);
      }
    }

    &_mobile {
      font-size: 20px;
    }
  }

  &__product-desc {
    width: 100%;
    height: 40px;
    overflow: hidden;
    font-family: Montserrat, sans-serif;
    color: #293241;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(40);
      font-size: vw-full(16);
    }

    &_popup {
      font-size: 14px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(14);
      }
    }
  }

  &__empty-products,
  &__empty-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    color: #7f7f7f;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(200);
      font-size: vw-full(16);
    }
  }
}
</style>
